import { useState, type FormEvent } from 'react'
import { Save } from 'lucide-react'
import { Button } from '../../core/index.js'
import type { AdminUser } from '../index.js'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface AdminEmailFormProps {
  admin: AdminUser
  /** The address to keep, or null to clear it. */
  onSave: (email: string | null) => void
  onCancel: () => void
  isPending: boolean
}

/**
 * The root sets where an admin is reached, its own address included: the
 * root's is where new feedback is emailed. It is a contact address beside
 * the login, which it never changes; left empty, it is cleared.
 */
export function AdminEmailForm({ admin, onSave, onCancel, isPending }: AdminEmailFormProps) {
  const [email, setEmail] = useState(admin.email ?? '')
  const trimmed = email.trim()
  const canSave = (trimmed === '' || EMAIL.test(trimmed)) && trimmed !== (admin.email ?? '') && !isPending

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (canSave) {
      onSave(trimmed === '' ? null : trimmed)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-1 flex flex-wrap items-center gap-1.5">
      <input
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="name@example.com"
        aria-label="Contact email"
        className="w-56 rounded border border-marine/30 bg-white px-2 py-1 text-[11px] text-ink"
        autoFocus
      />
      <Button type="submit" icon={Save} size="sm" disabled={!canSave}>
        {isPending ? 'Saving…' : trimmed === '' && admin.email ? 'Clear' : 'Save'}
      </Button>
      <button type="button" onClick={onCancel} disabled={isPending} className="text-[11px] font-bold text-ink-mid hover:text-ink disabled:opacity-40">
        Cancel
      </button>
    </form>
  )
}
